import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Smartphone, Download, ExternalLink, Chrome, Apple, CheckCircle2, Share2 } from "lucide-react";

// Simple detector for the beforeinstallprompt (Chrome/Edge/Samsung/Android)
function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  };
  return { canInstall: !!deferred, install };
}

export default function DownloadApp() {
  const { canInstall, install } = useInstallPrompt();
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const isMobile = /Android|iPhone|iPad/i.test(typeof navigator !== "undefined" ? navigator.userAgent : "");

  return (
    <AppLayout variant="user">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-nb-cyan">Mobile App</div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1">Take Next Billion With You</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">Install the Next Billion app on your phone — get instant access, notifications, and a native app feel. Two ways to install.</p>
      </div>

      {/* Option 1: One-tap PWA install (best experience — no APK) */}
      <div className="nb-card p-6 mb-4" data-testid="install-pwa">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="rounded-2xl border border-nb-cyan/30 bg-nb-cyan/10 p-4"><Smartphone size={28} className="text-nb-cyan"/></div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#8AE234]">✓ Recommended · No download · Instant</div>
            <div className="font-heading text-white text-xl mt-1">Install as App (PWA)</div>
            <p className="text-slate-400 text-sm mt-1 max-w-lg">Add Next Billion to your home screen. Looks and feels like a real app — no APK, no Play Store, no permission dialogs.</p>
            {canInstall ? (
              <button data-testid="pwa-install-btn" onClick={install} className="mt-4 px-5 py-2.5 rounded-xl bg-nb-cyan text-black font-heading font-bold hover:opacity-90"><Download size={14} className="inline mr-1"/>Install App</button>
            ) : (
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <StepCard icon={Chrome} title="On Android · Chrome / Edge / Samsung Internet"
                  steps={["Tap the ⋮ menu (top-right)","Choose 'Install app' or 'Add to Home screen'","Confirm — done!"]}/>
                <StepCard icon={Apple} title="On iPhone · Safari"
                  steps={["Tap the Share icon","Choose 'Add to Home Screen'","Confirm — done!"]}/>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Option 2: Download APK (via PWABuilder) */}
      <div className="nb-card p-6" data-testid="install-apk">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="rounded-2xl border border-[#EAB308]/30 bg-[#EAB308]/10 p-4"><Download size={28} className="text-[#EAB308]"/></div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#EAB308]">Advanced · Native APK</div>
            <div className="font-heading text-white text-xl mt-1">Download Android APK</div>
            <p className="text-slate-400 text-sm mt-1 max-w-lg">Prefer a real APK? Generate one via PWABuilder — free, official, and Play Store-ready.</p>

            <ol className="mt-4 space-y-2 text-sm text-slate-200">
              <li className="flex gap-2"><span className="text-nb-cyan font-mono">1.</span> Open <a href="https://www.pwabuilder.com/" target="_blank" rel="noreferrer" className="text-nb-cyan underline">PWABuilder.com <ExternalLink size={11} className="inline"/></a></li>
              <li className="flex gap-2"><span className="text-nb-cyan font-mono">2.</span> Paste your site URL:
                <code data-testid="pwa-url" className="ml-1 bg-black/40 border border-white/10 rounded px-2 py-0.5 text-xs font-mono text-white break-all">{currentOrigin}</code></li>
              <li className="flex gap-2"><span className="text-nb-cyan font-mono">3.</span> Click <span className="text-white">Start</span> → wait for the PWA analysis</li>
              <li className="flex gap-2"><span className="text-nb-cyan font-mono">4.</span> Click <span className="text-white">Package for Stores</span> → Choose <span className="text-white">Android</span></li>
              <li className="flex gap-2"><span className="text-nb-cyan font-mono">5.</span> Configure package (defaults work), click <span className="text-white">Download</span> — you get a signed <code className="bg-black/40 rounded px-1 text-xs">.apk</code> + <code className="bg-black/40 rounded px-1 text-xs">.aab</code></li>
              <li className="flex gap-2"><span className="text-nb-cyan font-mono">6.</span> Host the <code className="bg-black/40 rounded px-1 text-xs">.apk</code> file on your site OR upload to Google Play Console (the <code className="bg-black/40 rounded px-1 text-xs">.aab</code>)</li>
            </ol>

            <a data-testid="pwabuilder-link" href={`https://www.pwabuilder.com/reportcard?site=${encodeURIComponent(currentOrigin)}`} target="_blank" rel="noreferrer" className="mt-4 inline-block px-5 py-2.5 rounded-xl border border-[#EAB308]/40 text-[#EAB308] hover:bg-[#EAB308]/10 font-heading font-bold"><Download size={14} className="inline mr-1"/>Open PWABuilder for this site</a>
            <div className="mt-3 text-[11px] text-slate-500">💡 The APK is a Trusted Web Activity (TWA) — it wraps this site inside a native Android shell and is accepted by the Google Play Store.</div>
          </div>
        </div>
      </div>

      {/* Feature strip */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
        {[
          {t:"Instant login",   d:"Stay signed in on your device"},
          {t:"Home-screen icon",d:"Launch like a native app"},
          {t:"Full screen",     d:"No browser bars"},
          {t:"Works offline",   d:"Shell cached — resilient network"},
        ].map((f,i)=>(
          <div key={i} className="nb-card p-3">
            <CheckCircle2 size={14} className="text-[#8AE234] mx-auto"/>
            <div className="text-white text-sm mt-1">{f.t}</div>
            <div className="text-slate-500 text-[10px]">{f.d}</div>
          </div>
        ))}
      </div>

      {isMobile && (
        <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
          <Share2 size={12}/> Tip: In Safari, use the Share menu → "Add to Home Screen".
        </div>
      )}
    </AppLayout>
  );
}

function StepCard({ icon: Icon, title, steps }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0A1120] p-4">
      <div className="flex items-center gap-2 mb-2"><Icon size={14} className="text-nb-cyan"/><div className="text-xs text-white font-semibold">{title}</div></div>
      <ol className="space-y-1 text-xs text-slate-300">
        {steps.map((s,i)=><li key={i} className="flex gap-2"><span className="text-nb-cyan font-mono">{i+1}.</span>{s}</li>)}
      </ol>
    </div>
  );
}
